<template>
  <div class="login-container">
    <div class="login-box">
      <div class="login-header">
        <h2>元气管理系统</h2>
        <p>请登录您的账号</p>
      </div>
      
      <el-form
        ref="loginForm"
        :model="loginForm"
        :rules="loginRules"
        class="login-form"
        @submit.native.prevent="handleLogin"
      >
        <el-form-item prop="username">
          <el-input
            v-model="loginForm.username"
            placeholder="请输入登录名"
            prefix-icon="el-icon-user"
            size="large"
            @keyup.enter.native="handleLogin"
          />
        </el-form-item>
        
        <el-form-item prop="password">
          <el-input
            v-model="loginForm.password"
            type="password"
            placeholder="请输入密码"
            prefix-icon="el-icon-lock"
            size="large"
            show-password
            @keyup.enter.native="handleLogin"
          />
        </el-form-item>
        
        <el-form-item>
          <el-button
            type="primary"
            size="large"
            class="login-button"
            :loading="loading"
            @click="handleLogin"
          >
            {{ loading ? '登录中...' : '登录' }}
          </el-button>
        </el-form-item>
      </el-form>
    </div>
  </div>
</template>

<script>
import authAPI from '@/api/auth';
import { saveAuthInfo } from '@/utils/auth';
import logger from '@/utils/logger'

export default {
  name: 'Login',
  data() {
    return {
      loginForm: {
        username: '',
        password: ''
      },
      loginRules: {
        username: [
          { required: true, message: '请输入登录名', trigger: 'blur' }
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' }
        ]
      },
      loading: false
    };
  },
  mounted() {
    // 确保登录页面正确显示，不受scale-wrapper影响
    this.$nextTick(() => {
      const wrapper = document.querySelector('.scale-wrapper');
      const html = document.documentElement;
      if (wrapper) {
        wrapper.classList.add('no-scale');
      }
      if (html) {
        html.classList.add('admin-mode');
      }
    });
  },
  methods: {
    async handleLogin() {
      // 表单验证
      this.$refs.loginForm.validate(async (valid) => {
        if (!valid) {
          return false;
        }
        
        this.loading = true;
        
        try {
          const result = await authAPI.login(
            this.loginForm.username,
            this.loginForm.password
          );
          
          logger.debug('登录结果:', result);
          
          if (result.success && result.data) {
            const data = result.data;
            const token = data.token;
            const jti = data.jti;
            const exp = data.exp;
            const user = data.user;
            
            // 验证必要字段
            if (!token) {
              logger.error('登录响应缺少token字段');
              this.$message.error('登录失败：服务器返回数据异常');
              return;
            }
            
            if (!user) {
              logger.error('登录响应缺少user字段');
              this.$message.error('登录失败：服务器返回数据异常');
              return;
            }
            
            logger.debug('保存登录信息:', { 
              token: token ? '已获取' : '缺失', 
              jti, 
              exp,
              userId: user.id,
              loginName: user.loginName,
              realName: user.realName,
              storesCount: user.stores ? user.stores.length : 0
            });
            
            // 保存完整的登录数据：{ token, jti, exp, user: {...} }
            const loginData = {
              token,
              jti,
              exp,
              user
            };
            
            saveAuthInfo(token, loginData);
            
            // 验证token是否保存成功
            const savedToken = localStorage.getItem('auth_token');
            if (!savedToken || savedToken !== token) {
              logger.error('Token保存失败！', { savedToken, expectedToken: token });
              this.$message.error('登录信息保存失败，请重试');
              return;
            }
            
            logger.debug('Token保存成功，准备跳转');
            
            // 显示成功消息
            this.$message.success('登录成功');
            
            // 延迟一下再跳转，确保消息显示和token保存完成
            setTimeout(() => {
              // 跳转：优先使用redirect参数，否则跳转到元气管理系统
              const redirect = this.$route.query.redirect;
              if (redirect) {
                logger.debug('跳转到redirect路径:', redirect);
                this.$router.push(redirect).catch(err => {
                  logger.error('跳转失败:', err);
                  // 如果跳转失败，尝试跳转到admin
                  this.$router.push('/admin');
                });
              } else {
                // 登录成功后跳转到元气管理系统
                logger.debug('跳转到管理页面: /admin');
                this.$router.push('/admin').catch(err => {
                  logger.error('跳转到管理页面失败:', err);
                });
              }
            }, 300);
          } else {
            // 处理登录失败
            let errorMsg = result.message || '登录失败，请检查用户名和密码';
            
            // 根据错误码显示不同的错误信息
            if (result.code === 401) {
              errorMsg = '用户名或密码错误';
            } else if (result.code === 403) {
              errorMsg = '账户已禁用，请联系管理员';
            }
            
            logger.error('登录失败:', errorMsg);
            this.$message.error(errorMsg);
          }
        } catch (error) {
          logger.error('登录异常:', error);
          this.$message.error('登录失败，请稍后重试');
        } finally {
          this.loading = false;
        }
      });
    }
  }
};
</script>

<style scoped>
.login-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  overflow: hidden;
  z-index: 1000;
}

.login-box {
  width: 420px;
  padding: 40px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

.login-header {
  text-align: center;
  margin-bottom: 40px;
}

.login-header h2 {
  margin: 0 0 10px 0;
  font-size: 28px;
  color: #333;
  font-weight: 600;
}

.login-header p {
  margin: 0;
  font-size: 14px;
  color: #999;
}

.login-form {
  margin-top: 30px;
}

.login-form .el-form-item {
  margin-bottom: 24px;
}

.login-button {
  width: 100%;
  margin-top: 10px;
  font-size: 16px;
  height: 48px;
}

.login-form .el-input__inner {
  height: 48px;
  line-height: 48px;
}
</style>

